import * as T from 'three';
// Sweets packed inside a breakable shell, as spheres with position-based
// contacts (Müller et al., Position Based Dynamics): the floor, each other, and
// the shell, which holds them only where its piece is still in place. Packed
// sweets stay put until a hole opens near them; then they spring out of it and
// fall. When most of the shell is gone, the rest come loose and tumble out.
// Spin follows rolling, so they tumble as they go.
export class CandyPile{
  // shell: {center, radii:[a,b,c]} in toy space; pieces: unit directions in the
  // shell's normalised space; open(i) says whether piece i has broken off.
  constructor({sweets,shell,pieces,open,gravity=12,random=Math.random}){
    this.shell=shell;this.pieces=pieces;this.open=open;this.gravity=gravity;this.random=random;
    this.items=sweets.map(({center,radius})=>({rest:[...center],r:radius,p:[...center],prev:[...center],q:new T.Quaternion(),w:[0,0,0],inside:true,held:true}));
    this.tmp=new T.Vector3();this.spin=new T.Quaternion();this.euler=new T.Euler();this.activity=1;
  }
  // Direction of a point in the shell's normalised (unit sphere) space and its distance.
  local(p){const s=this.shell,x=(p[0]-s.center[0])/s.radii[0],y=(p[1]-s.center[1])/s.radii[1],z=(p[2]-s.center[2])/s.radii[2],d=Math.hypot(x,y,z)||1e-9;return {dir:[x/d,y/d,z/d],d};}
  nearest(dir){let best=0,dot=-2;for(let i=0;i<this.pieces.length;i++){const q=this.pieces[i],k=q[0]*dir[0]+q[1]*dir[1]+q[2]*dir[2];if(k>dot){dot=k;best=i;}}return best;}
  // The wall holds where the two pieces nearest a direction are both whole.
  // A large sweet at the edge of a hole overlaps the next piece too, so it
  // may pass where either one has broken.
  walled(dir){
    let a=-1,b=-1,da=-2,db=-2;
    for(let i=0;i<this.pieces.length;i++){const q=this.pieces[i],k=q[0]*dir[0]+q[1]*dir[1]+q[2]*dir[2];if(k>da){b=a;db=da;a=i;da=k;}else if(k>db){b=i;db=k;}}
    return !this.open(a)&&!(b>=0&&this.open(b));
  }
  // Packed sweets near a new hole spring out of it, harder the closer they are.
  burst(piece,strength=3.2){
    const q=this.pieces[piece],s=this.shell,hole=[0,1,2].map(a=>s.center[a]+q[a]*s.radii[a]),reach=Math.min(s.radii[0],s.radii[1])*.75;
    const out=[q[0]*s.radii[0],q[1]*s.radii[1],q[2]*s.radii[2]],l=Math.hypot(...out);
    for(const c of this.items){
      if(!c.inside)continue;const d=Math.hypot(c.p[0]-hole[0],c.p[1]-hole[1],c.p[2]-hole[2]);if(d>reach)continue;c.held=false;
      // Toward the hole and on outward, faster nearer to it.
      const kick=strength*(.55+.45*(1-d/reach))*(.8+.4*this.random()),to=[hole[0]-c.p[0],hole[1]-c.p[1],hole[2]-c.p[2]],tl=Math.hypot(...to)||1;
      const v=[0,1,2].map(a=>(to[a]/tl*.6+out[a]/l*.4)*kick+(a===1?kick*.5:0));
      for(let a=0;a<3;a++)c.prev[a]=c.p[a]-v[a]/120;
      c.w=[(this.random()-.5)*14,(this.random()-.5)*14,(this.random()-.5)*14];
    }
    this.activity=1;
  }
  // Everything left inside comes loose.
  // With the shell mostly gone, everything left spills out over the rim.
  // Once: they hop high enough to clear what is left of the bowl.
  loosen(strength=1.8){
    if(this.loose)return;this.loose=true;const s=this.shell;
    for(const c of this.items){
      if(!c.inside)continue;c.held=false;
      // Sweets near the middle are thrown farther, so they clear the rim too.
      const d=[c.p[0]-s.center[0],0,c.p[2]-s.center[2]],l=Math.hypot(...d)||1,near=1-Math.min(1,l/s.radii[0]);
      const k=strength*(.9+.9*near)*(.85+.3*this.random()),up=Math.sqrt(2*this.gravity*s.radii[1]*.8)*(.95+.25*this.random());
      if(l<1e-3){d[0]=this.random()-.5;d[2]=this.random()-.5;}
      const v=[d[0]/l*k,up,d[2]/l*k];for(let a=0;a<3;a++)c.prev[a]=c.p[a]-v[a]/120;
      c.w=[(this.random()-.5)*12,(this.random()-.5)*12,(this.random()-.5)*12];
    }
    this.activity=1;
  }
  // A bump from below: loose sweets jump and tumble, then settle again.
  hop(height=.45){
    const s=this.shell;
    for(const c of this.items){
      if(c.held)continue;
      const up=Math.sqrt(2*this.gravity*s.radii[1]*height*(.6+.8*this.random())),a=this.random()*Math.PI*2,side=up*.25*this.random();
      const v=[Math.cos(a)*side,up,Math.sin(a)*side];for(let k=0;k<3;k++)c.prev[k]=c.p[k]-v[k]/120;
      c.w=[(this.random()-.5)*14,(this.random()-.5)*14,(this.random()-.5)*14];
    }
    this.activity=1;
  }
  get packed(){return this.items.filter(c=>c.held).length;}
  step(dt){
    if(!(dt>0)||!this.activity)return;
    const h=Math.min(dt,1/30)/2,s=this.shell;let motion=0;
    for(let sub=0;sub<2;sub++){
      for(const c of this.items){
        if(c.held){c.prev=[...c.p];continue;}
        const v=[0,1,2].map(a=>(c.p[a]-c.prev[a])*.995);
        c.prev=[...c.p];c.p[0]+=v[0];c.p[1]+=v[1]-this.gravity*h*h;c.p[2]+=v[2];
      }
      for(let it=0;it<3;it++){
        for(const c of this.items){
          if(c.held)continue;
          // Floor.
          if(c.p[1]<c.r){c.p[1]=c.r;const rub=.25;c.prev[0]+=(c.p[0]-c.prev[0])*rub;c.prev[2]+=(c.p[2]-c.prev[2])*rub;}
          // Shell wall: an intact piece blocks from whichever side the sweet is
          // on; through a hole it may cross, in or out.
          const {dir,d}=this.local(c.p),intact=this.walled(dir),thin=c.r/Math.min(s.radii[0],s.radii[1]);
          if(intact){
            if(c.inside&&d>1-thin)for(let a=0;a<3;a++)c.p[a]=s.center[a]+dir[a]*s.radii[a]*(1-thin);
            else if(!c.inside&&d<1+thin)for(let a=0;a<3;a++)c.p[a]=s.center[a]+dir[a]*s.radii[a]*(1+thin);
          }else c.inside=d<1;
        }
        // Sweets push each other apart.
        for(let i=0;i<this.items.length;i++)for(let j=i+1;j<this.items.length;j++){
          const a=this.items[i],b=this.items[j],wa=a.held?0:1,wb=b.held?0:1;if(!wa&&!wb)continue;
          const dx=b.p[0]-a.p[0],dy=b.p[1]-a.p[1],dz=b.p[2]-a.p[2],dd=Math.hypot(dx,dy,dz),min=a.r+b.r;
          if(dd>=min||dd<1e-9)continue;const k=(min-dd)/dd/(wa+wb);
          a.p[0]-=dx*k*wa;a.p[1]-=dy*k*wa;a.p[2]-=dz*k*wa;b.p[0]+=dx*k*wb;b.p[1]+=dy*k*wb;b.p[2]+=dz*k*wb;
        }
      }
      for(const c of this.items){
        if(c.held)continue;
        const vx=(c.p[0]-c.prev[0])/h,vz=(c.p[2]-c.prev[2])/h;motion=Math.max(motion,Math.hypot(vx,(c.p[1]-c.prev[1])/h,vz));
        // On the floor a sweet rolls; in the air it keeps its tumble.
        if(c.p[1]<=c.r+1e-4){c.w=[vz/c.r,c.w[1]*.9,-vx/c.r];}
        const wl=Math.hypot(...c.w);
        if(wl>1e-6){this.spin.setFromAxisAngle(this.tmp.set(c.w[0]/wl,c.w[1]/wl,c.w[2]/wl),wl*h);c.q.premultiply(this.spin).normalize();}
        if(c.p[1]<=c.r+1e-4)c.w=c.w.map(w=>w*.98);
      }
    }
    this.activity=motion>.02?1:0;
  }
  // Mesh transform that carries the rest geometry to the sweet's pose.
  pose(i,mesh){
    const c=this.items[i];mesh.quaternion.copy(c.q);
    this.tmp.fromArray(c.rest).applyQuaternion(c.q);mesh.position.set(c.p[0]-this.tmp.x,c.p[1]-this.tmp.y,c.p[2]-this.tmp.z);
  }
  get outside(){return this.items.filter(c=>!c.inside).length;}
  reset(){for(const c of this.items){c.p=[...c.rest];c.prev=[...c.rest];c.q.identity();c.w=[0,0,0];c.inside=true;c.held=true;}this.loose=false;this.activity=1;}
}
